import React from 'react';
import { Server } from 'lucide-react';
import LegalPageLayout from '../components/legal/LegalPageLayout';
import LegalLink from '../components/legal/LegalLink';
import {
  legalBodyText,
  legalLinkClass,
  legalTableCell,
  legalTableHead,
  legalTableRow,
} from '../components/legal/legalStyles';

const subprocessors = [
  {
    name: 'Google Cloud / Firebase / Gemini',
    purpose: 'Application hosting, authentication, and AI/LLM processing',
    dataProcessed: 'Account data, integration content, embeddings inputs/outputs',
    location: 'United States / EU (region-dependent)',
    link: 'https://cloud.google.com/terms/data-processing-addendum',
  },
  {
    name: 'Pinecone',
    purpose: 'Vector database for semantic search and RAG',
    dataProcessed: 'Embedded text chunks with tenant metadata',
    location: 'United States',
    link: 'https://www.pinecone.io/legal/dpa/',
  },
  {
    name: 'Stripe',
    purpose: 'Subscription billing and payment processing',
    dataProcessed: 'Billing contact details, payment method metadata',
    location: 'United States / EU',
    link: 'https://stripe.com/legal/dpa',
  },
  {
    name: 'Atlassian (Jira)',
    purpose: 'Customer-connected issue tracking via OAuth',
    dataProcessed: 'Jira issues, users, projects authorized by customer',
    location: 'Varies by customer Atlassian site',
    link: 'https://www.atlassian.com/legal/privacy-policy',
  },
  {
    name: 'Slack',
    purpose: 'Customer-connected messaging via OAuth',
    dataProcessed: 'Messages, channels, users authorized by customer',
    location: 'Varies by customer Slack workspace',
    link: 'https://slack.com/trust/privacy/privacy-policy',
  },
  {
    name: 'Zendesk',
    purpose: 'Customer-connected support tickets via OAuth',
    dataProcessed: 'Tickets, users, organizations authorized by customer',
    location: 'Varies by customer Zendesk instance',
    link: 'https://www.zendesk.com/company/agreements-and-terms/privacy-notice/',
  },
  {
    name: 'Linear, Trello, Monday.com, Google Drive',
    purpose: 'Customer-connected work management and document sources',
    dataProcessed: 'Issues, cards, items, and documents authorized by customer',
    location: 'Varies by customer account region',
    link: 'https://edyt.io/privacy',
  },
];

const SubprocessorsPage: React.FC = () => {
  return (
    <LegalPageLayout
      title="Subprocessors"
      lastUpdated="June 2026"
      icon={<Server className="w-6 h-6 text-white" />}
      iconGradientClassName="from-indigo-600 to-blue-600"
      cardTitle="Edyt Subprocessor List"
      maxWidthClassName="max-w-5xl"
      defaultBack={{ href: '/privacy', label: 'Back to Privacy Policy' }}
      footer={{
        href: '/privacy',
        label: 'Back to Privacy Policy',
        buttonGradientClassName:
          'from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700',
      }}
    >
      <div className={`${legalBodyText} space-y-4`}>
        <p>
          Edyt uses the following subprocessors to deliver the service. Customer-connected
          integrations process data only when you authorize them. This list is provided for
          transparency and GDPR Article 28 compliance. Legal review is recommended before
          relying on this list for contractual purposes.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className={`${legalTableRow} text-left`}>
                <th className={legalTableHead}>Subprocessor</th>
                <th className={legalTableHead}>Purpose</th>
                <th className={legalTableHead}>Data Processed</th>
                <th className={legalTableHead}>Location</th>
                <th className={legalTableHead}>Vendor DPA / Privacy</th>
              </tr>
            </thead>
            <tbody>
              {subprocessors.map((row) => (
                <tr key={row.name} className={legalTableRow}>
                  <td className={`${legalTableCell} font-medium`}>{row.name}</td>
                  <td className={legalTableCell}>{row.purpose}</td>
                  <td className={legalTableCell}>{row.dataProcessed}</td>
                  <td className={legalTableCell}>{row.location}</td>
                  <td className={legalTableCell}>
                    <a
                      href={row.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={legalLinkClass}
                    >
                      View policy
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400">
          For a signed Data Processing Agreement with Edyt, see our{' '}
          <LegalLink to="/dpa">DPA summary</LegalLink>.
        </p>
      </div>
    </LegalPageLayout>
  );
};

export default SubprocessorsPage;
